import { ReactNode, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { ApolloQueryResult } from '@apollo/client'
import { Box, Form } from '@factorialco/deprecated-design-system'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  AllocationFragment,
  EvaluateExpensableQuery,
  EvaluateExpensableQueryVariables,
  ExpensableBudgetFragment,
  ExpenseDetailPageQuery,
  GetExpensablesQuery,
  SpendingAlertFragment,
} from 'generated/resources/graphql'
import { CurrencySelectorCurrencyFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import SubmitOcrAnimation from 'modules/expenses/components/common/SubmitOcrAnimation'
import ConfirmationScreen from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen'
import EditExpenseFooterActions from 'modules/expenses/components/expenses/Detail/_Form/Common/EditExpenseFooterActions'
import SnackBarAlerts from 'modules/expenses/components/expenses/Detail/_Form/Common/SnackBarAlerts'
import { getExpensesFormDefaultValues } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/form'
import { ExpenseSchema } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/schema'
import { useBulkActions } from 'modules/expenses/components/expenses/Detail/hooks/useBulkActions'
import { useFilesPreviewer } from 'modules/expenses/components/expenses/Detail/hooks/useFilesPreviewer'
import { SpendingPolicesResultData } from 'modules/expenses/graphql/types'
import {
  ExpensableExpense,
  normalizeEffectiveOn,
  useDelete,
  useSendForApproval,
  useUpdate,
  useUpdateAndApprove,
  useUpdateFromDocument,
} from 'modules/expenses/hooks/useExpensable'
import { useExpenseAlerts } from 'modules/expenses/hooks/useExpenseAlerts'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { useProjectsAssignedToEmployees } from 'modules/expenses/hooks/useProjects'
import { SignableOrganizationPerCountry } from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { filterCustomizationFieldsByExpenseDocumentType } from 'modules/expenses/lib/customization_settings/filterByExpenseDocumentType'
import {
  isApprovedExpensable,
  isDraftExpensable,
  isManualExpensable,
  isPendingExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { buildEvaluatePolicyData } from 'modules/expenses/lib/spending_policies/evaluatePolicy'
import { Expensable } from 'modules/expenses/types/Expensable'
import { useBudgetOptions } from 'modules/finance/hooks/useBudgets'

import { shouldShowCostCenterBlock } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/CostCenters/form/schema'
import { shouldShowBlock as shouldShowBudgetBlock } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Budget/form/schema'
import { shouldShowBlock as shouldShowProjectsBlock } from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Projects/form/schema'
import { fieldEnabled } from 'modules/expenses/hooks/useExpensesCustomization'

import NewExpenseForm, { ExpenseScopedEditSection, isExpenseScopedEditSection } from './new'

// Ensures the scoped-edit section fills most of the viewport so the sticky
// footer doesn't overlap short sections.
const SCOPED_EDIT_MIN_HEIGHT = 'calc(100dvh - 200px)'

type ViewMode =
  | { type: 'confirmation' }
  | { type: 'fullEdit'; scrollTo?: string }
  | { type: 'sectionEdit'; section: ExpenseScopedEditSection }

interface ExpenseFormProps {
  expensable: Expensable
  expense: ExpensableExpense
  customizationFields: CustomizationField[]
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  adeTermsAccepted?: boolean
  editable: boolean
  approvalsInformation?: ReactNode
  payrollSupplement: ReactNode
  onClose: () => void
  hasReportOnBehalfFeature: boolean
  hasProjectsFeature: boolean
  reimburseThroughPayrollEnabled: boolean
  reimburseThroughSepaEnabled: boolean
  refetchExpensable: () => Promise<ApolloQueryResult<GetExpensablesQuery>>
  refetchSpendingPolicyEvaluate?: (
    variables: EvaluateExpensableQueryVariables
  ) => Promise<ApolloQueryResult<EvaluateExpensableQuery>>
  setSpendingPolicyEvaluationResult: (policy: SpendingPolicesResultData | undefined) => void
  canUseSpendingPolicies: boolean
  canCreateExpensesForOthers: boolean
  canOverrideApprovalFlow: boolean
  canSetExpensableToPaid: boolean
  canReviewExpensable: boolean
  canCustomizeSettings: boolean
  canSeeEmployeeLegalEntity: boolean
  currencies?: CurrencySelectorCurrencyFragment[]
  canDeleteExpensable: boolean
  onClickSnackBar: () => void
  toggleAlert: () => void
  hideAlert: boolean
  budget?: ExpensableBudgetFragment | null
  budgetAllocation?: AllocationFragment | null
  spendingPolicyEvaluationResult?: SpendingPolicesResultData | null
  spendingAlerts?: SpendingAlertFragment[]
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
  approvalsV2Enabled: boolean
  approvalProcess: ApprovalProcessType | null
}

type ApprovalProcessType =
  ExpenseDetailPageQuery['expenses']['expensablesConnection']['edges'][0]['node']['approvalProcess']

const ExpenseForm = ({
  expensable,
  expense,
  customizationFields: unfilteredCustomizationFields,
  taxAgencyOrganization,
  adeTermsAccepted,
  editable,
  approvalsInformation,
  onClose,
  hasReportOnBehalfFeature,
  hasProjectsFeature,
  reimburseThroughPayrollEnabled,
  reimburseThroughSepaEnabled,
  refetchExpensable,
  refetchSpendingPolicyEvaluate,
  setSpendingPolicyEvaluationResult,
  canUseSpendingPolicies,
  canCreateExpensesForOthers,
  canOverrideApprovalFlow,
  canSetExpensableToPaid,
  canReviewExpensable,
  canCustomizeSettings,
  canSeeEmployeeLegalEntity,
  currencies,
  canDeleteExpensable,
  onClickSnackBar,
  toggleAlert,
  hideAlert,
  budget,
  budgetAllocation,
  spendingPolicyEvaluationResult,
  spendingAlerts,
  registerDropHandler,
  approvalsV2Enabled,
  approvalProcess,
}: ExpenseFormProps) => {
  const { employee: currentEmployee } = useCurrentSession()

  const bulkActions = useBulkActions()
  const previewer = useFilesPreviewer()

  const { loading: updating } = useUpdate()
  const { loading: updatingAndApproving } = useUpdateAndApprove()
  const { loading: updatingFromDocument } = useUpdateFromDocument()
  const { loading: sendingForApproval } = useSendForApproval()
  const { loading: deleting } = useDelete()

  const [documentType, setDocumentType] = useState<string | undefined>(
    expense?.documentType ?? undefined
  )

  const customizationFields = useUnsafeMemo(
    () =>
      filterCustomizationFieldsByExpenseDocumentType(unfilteredCustomizationFields, documentType),
    [unfilteredCustomizationFields, documentType]
  )

  const isManual = isManualExpensable(expensable)
  const isDraft = isDraftExpensable(expensable)

  const submitting = updating || updatingAndApproving || sendingForApproval || deleting

  const isDraftManual = expensable && isManual && isDraft
  const syncableState = expensable?.syncableState?.[0] ?? null

  const { alertMessage, alertType } = useExpenseAlerts({
    expensableType: expensable?.type ?? 'expense',
    isDraftManual: isDraftManual ?? false,
    expensableAmount: Number(expensable.amount),
    reimbursableAmount: Number(expensable.reimbursableAmount),
    budget,
    budgetAllocation,
    spendingPolicy: spendingPolicyEvaluationResult,
    spendingAlerts,
    syncableState,
  })

  const showSnackBar =
    alertMessage.length > 0 &&
    !hideAlert &&
    canUseSpendingPolicies &&
    expensable &&
    (isDraft || isPendingExpensable(expensable) || isApprovedExpensable(expensable))

  const hasSignedDocuments = !!expensable?.expense?.signedDocument?.id

  const defaultValues = getExpensesFormDefaultValues({
    employee: currentEmployee,
    customizationFields,
    groupId: expensable?.groupId,
    expensable,
  })
  const [formEffectiveOn, setFormEffectiveOn] = useState(defaultValues.effectiveOn)
  const [formEmployeeId, setFormEmployeeId] = useState<string | null | undefined>(
    defaultValues.employeeId
  )
  const { employee } = useCurrentSession()
  const employeeId = formEmployeeId ?? employee?.id

  const { projectWorkers: newProjectWorkers, loading: newLoadingProjectWorkers } =
    useProjectsAssignedToEmployees({
      skip: false,
      employeeIds: employeeId ? [employeeId] : [],
    })

  const { budgetOptions: newBudgetOptions, loading: newLoadingBudgetOptions } = useBudgetOptions({
    effectiveOn: normalizeEffectiveOn(formEffectiveOn),
    employeeId,
    skip: false,
  })

  const expenseForm = useForm({
    resolver: zodResolver(
      ExpenseSchema({
        customizationFields,
        hasSignedDocuments,
        previewer,
        budgetOptions: newBudgetOptions,
        projectWorkers: newProjectWorkers,
        hasProjectsFeature,
        resourceType: 'expense',
      })
    ),
    defaultValues: defaultValues,
    mode: 'onChange',
  })
  const { formState, trigger } = expenseForm
  const [viewMode, setViewMode] = useState<ViewMode>({ type: 'fullEdit' })
  const [hasValidatedInitially, setHasValidatedInitially] = useState(false)

  const { isValid: isValidForm, isDirty } = formState

  useEffect(() => {
    if (!canUseSpendingPolicies || !refetchSpendingPolicyEvaluate) return

    const formValues = expenseForm.getValues()
    const formIsUpdated = expenseForm.formState.isDirty

    if (formIsUpdated) {
      const reimbursableAmount = Number(formValues.reimbursableAmount ?? 0)
      refetchSpendingPolicyEvaluate({
        amount:
          reimbursableAmount !== 0
            ? reimbursableAmount * 100
            : Number(formValues.amount ?? 0) * 100,
        currency: formValues.currency,
        effectiveOn: expensable?.effectiveOn || undefined,
        category: formValues.category || '',
        subcategory:
          formValues.subcategory && formValues.subcategory !== ''
            ? formValues.subcategory
            : undefined,
        companyId: String(expensable.company.id),
        expensableId: expensable.id,
        employeeId: String(formValues.employee?.id),
      }).then((result: ApolloQueryResult<EvaluateExpensableQuery>) => {
        const policyEvaluation =
          result?.data?.finance.expenseSpendingPolicyEvaluationsConnection.nodes[0]

        const policyData = buildEvaluatePolicyData(policyEvaluation)
        setSpendingPolicyEvaluationResult(policyData)
      })
    }
  }, [
    canUseSpendingPolicies,
    refetchSpendingPolicyEvaluate,
    expenseForm,
    expensable.company.id,
    expensable?.effectiveOn,
    expensable.id,
    setSpendingPolicyEvaluationResult,
  ])

  const onDocumentTypeChange = (value: string) => {
    setDocumentType(value)
  }

  const onEffectiveOnChange = (value: string) => {
    setFormEffectiveOn(value)
  }

  // On mount, validate the form once to decide the initial view.
  // If all fields pass validation, jump straight to the confirmation screen;
  // otherwise stay in fullEdit mode so the user can fill in missing data.
  // The hasValidatedInitially guard ensures this runs exactly once —
  // without it, changes to customizationFields or trigger would re-fire
  // and could snap the user back to confirmation after they've navigated away.
  useUnsafeEffect(() => {
    if (hasValidatedInitially) return
    trigger().then((validationResult) => {
      if (validationResult) {
        setViewMode({ type: 'confirmation' })
      }
      // else stays on fullEdit
      setHasValidatedInitially(true)
    })
  }, [customizationFields, trigger, hasValidatedInitially])

  const isSubmitting = submitting || bulkActions.submitting
  const showApprovalsInformation = !isSubmitting && !isDraft

  const showBudgetAndProject =
    fieldEnabled(customizationFields, 'expense', 'budget_id') ||
    fieldEnabled(customizationFields, 'expense', 'projects')
  const canEditBudgetAndProject =
    shouldShowBudgetBlock(newBudgetOptions, customizationFields, 'expense') ||
    shouldShowProjectsBlock(hasProjectsFeature, newProjectWorkers, customizationFields, 'expense')
  const showCostCenters = shouldShowCostCenterBlock(customizationFields, 'expense')
  const showDescription = fieldEnabled(customizationFields, 'expense', 'description')
  const showInternalReference = fieldEnabled(customizationFields, 'expense', 'internal_reference')

  return (
    <Form padding="s0" notRounded>
      <Box
        gap="s32"
        paddingX="s24"
        style={viewMode.type === 'sectionEdit' ? { minHeight: SCOPED_EDIT_MIN_HEIGHT } : undefined}
      >
        <FormProvider {...expenseForm}>
          {viewMode.type === 'confirmation' ? (
            <ConfirmationScreen
              expensable={expensable}
              customizationFields={customizationFields}
              resourceType="expense"
              onEdit={(scrollToSection?: string | undefined) => {
                if (scrollToSection && isExpenseScopedEditSection(scrollToSection)) {
                  setViewMode({ type: 'sectionEdit', section: scrollToSection })
                } else {
                  setViewMode({ type: 'fullEdit', scrollTo: scrollToSection })
                }
              }}
              editable={editable}
              adeTermsAccepted={adeTermsAccepted}
              taxAgencyOrganization={taxAgencyOrganization}
              showBudgetAndProject={showBudgetAndProject}
              canEditBudgetAndProject={canEditBudgetAndProject}
              showCostCenters={showCostCenters}
              showDescription={showDescription}
              showInternalReference={showInternalReference}
            />
          ) : (
            <NewExpenseForm
              customizationFields={customizationFields}
              canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
              canEditExpensesWithPendingStatus={editable}
              canCustomizeSettings={canCustomizeSettings}
              canCreateExpensesForOthers={canCreateExpensesForOthers}
              currencies={currencies}
              expensable={expensable}
              refetchExpensable={refetchExpensable}
              hasProjectsFeature={hasProjectsFeature}
              hasReportOnBehalfFeature={hasReportOnBehalfFeature}
              taxAgencyOrganization={taxAgencyOrganization}
              projectWorkers={newProjectWorkers}
              budgetOptions={newBudgetOptions}
              loadingProjectWorkers={newLoadingProjectWorkers}
              loadingBudgetOptions={newLoadingBudgetOptions}
              onDocumentTypeChange={onDocumentTypeChange}
              onEmployeeIdChange={setFormEmployeeId}
              onEffectiveOnChange={onEffectiveOnChange}
              scrollTo={viewMode.type === 'fullEdit' ? viewMode.scrollTo : undefined}
              registerDropHandler={registerDropHandler}
              editSection={viewMode.type === 'sectionEdit' ? viewMode.section : undefined}
              onBackToConfirmation={() => setViewMode({ type: 'confirmation' })}
            />
          )}
        </FormProvider>
        {showApprovalsInformation && viewMode.type !== 'sectionEdit' && approvalsInformation}
      </Box>
      {updatingFromDocument && (
        <Modal illustration="document-folder-disorder-list" title="" locked>
          <Box
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="full"
            gap="s6"
            tabIndex={0}
          >
            <SubmitOcrAnimation operation="update" />
          </Box>
        </Modal>
      )}
      <SnackBarAlerts
        showSnackBar={showSnackBar}
        alertType={alertType}
        alertMessage={alertMessage}
        toggleAlert={toggleAlert}
        onClickLink={onClickSnackBar}
      />
      <EditExpenseFooterActions
        formType="expense"
        expensable={expensable}
        formValues={expenseForm.getValues()}
        customizationFields={customizationFields}
        canEdit={editable}
        canReviewExpensable={canReviewExpensable}
        canDeleteExpensable={canDeleteExpensable}
        canOverrideApprovalFlow={canOverrideApprovalFlow}
        canSetExpensableToPaid={canSetExpensableToPaid}
        reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
        reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
        taxAgencyOrganization={taxAgencyOrganization}
        formValid={isValidForm}
        formDirty={isDirty}
        approvalsV2Enabled={approvalsV2Enabled}
        approvalProcess={approvalProcess}
        trigger={trigger}
        onClose={onClose}
        onAfterSave={() => {
          expenseForm.setValue('files', (expenseForm.getValues('files') ?? []).filter((f: { id?: string }) => 'id' in f))
          expenseForm.setValue('filesIdToRemove', [])
        }}
        isConfirmation={viewMode.type === 'confirmation'}
        isScopedEdit={viewMode.type === 'sectionEdit'}
        onBackToConfirmation={() => setViewMode({ type: 'confirmation' })}
      />
    </Form>
  )
}
export default observer(ExpenseForm)
